import React, { useState, useRef } from "react"
import { Container } from "react-bootstrap"
import { Map, Marker } from "pigeon-maps"

import {
  Box,
  Grid,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"

import InfoCard from "./Card.js"

import * as styles from "./Map.module.scss"

import localityMapping from "../../data/locality_mapping.json"

// Refer to data/locality_mapping.json for the locality code
const markerColor = {
  L: "rgb(165, 80, 112)", // Locality
  K: "rgb(93, 57, 174)", // Kinship
	f: "rgb(120, 245, 66)",
  y: "rgb(208, 34, 227)",
  h: "rgb(40, 0, 219)",
  c: "rgb(242, 0, 57)",
  k: "rgb(242, 230, 0)",
  s: "rgb(191, 135, 52)",
  o: "rgb(28, 28, 28)",
  "": "rgb(87, 87, 87)", // default
}
const selectedMarkerColor = "#eee"
const singaporeCoordinates = [1.3521, 103.8198]
const oldestYear = 1820
const currentYear = new Date().getFullYear()


const MapComponent = ({ location, data : mapData }) => {
  // GraphQL query data
	const [originalData, legendsCode] = mapData

  const legends = legendsCode.map(loc => ({ key: loc, text: localityMapping[loc], color: markerColor[loc] }))

  // React State
	const [selectedYearRange, setSelectedYearRange] = useState([oldestYear, currentYear])
  const [filterType, setFilterType]   = useState("")
  const [filterLocality, setFilterLocality] = useState(Object.fromEntries(legendsCode.map(loc => [loc, true])))
  const [selectedClan, setSelectedClan] = useState(null)
  const mapContainerRef = useRef(null);

	function handleChangeYear(event, yearRange) {
    // Prevent updates for non-effective slide action
		if (yearRange === selectedYearRange) return;

    setSelectedYearRange(yearRange)
	}

  function handleFilterLocality(locality) {
    setFilterLocality({ ...filterLocality, [locality]: !filterLocality[locality] })
  }

  function allClanData() {
    return originalData
      .filter(clan => filterType === "" || clan.type === filterType)
      .filter(clan => clan.type === "L" ? filterLocality[clan.localityCode] : true)
      .filter(({ yearest }) => {
        return yearest === null || (yearest >= selectedYearRange[0] && yearest <= selectedYearRange[1])
      })
  }


  // Handle location state from index pages
  if (location.state) {
    const [filteredClan] = originalData.filter(clan =>
      clan.id === location.state.clanId && clan.type === location.state.clanType);

    location.state = null;
    setSelectedClan(filteredClan);
  }

	return (
		<Container className={styles.mapContainer}>

		<section ref={mapContainerRef}>
    {selectedClan &&
			<InfoCard
				data={selectedClan}
				setData={setSelectedClan}
				enabled={!!selectedClan}
				container={mapContainerRef.current}
			/>
    }

			<ToggleButtonGroup
				className={styles.toggleButtonGroup}
				value={filterType}
				exclusive
				onChange={(_, type) => {
					setSelectedClan(null)
					setFilterType(type)
				}}
				aria-label="Display Clans"
			>
				<ToggleButton value="L" aria-label="locality">
					Locality
				</ToggleButton>
				<ToggleButton value="K" aria-label="kinship">
					Kinship
				</ToggleButton>
				<ToggleButton value="" aria-label="all">
					All
				</ToggleButton>
			</ToggleButtonGroup>


			<Map
				height={600}
				defaultWidth={800}
				defaultCenter={selectedClan
						? [selectedClan.latitude, selectedClan.longitude]
						: singaporeCoordinates}
				defaultZoom={selectedClan ? 18 : 11}
				metaWheelZoom={true}
			>
				{allClanData().map(data => {
					const { id, latitude, longitude, type, localityCode } = data;
					return (
						<Marker
							key={type + id}
							width={40}
							anchor={[latitude, longitude]}
							color={
								(({ id : selectedId = null, type : selectedType }) =>
									selectedId === id && selectedType === type
										? selectedMarkerColor
									: filterType === ""
										? markerColor[type]
									: filterType === "L"
										? markerColor[localityCode]
									: markerColor.K // kinship color
								)(selectedClan || {})
							}
							onClick={() => setSelectedClan(data)}
						/>
					)
				})}
			</Map>
		</section>



		{filterType === "L" &&
			<Box
				className={styles.legends}
			>

				{legends.map(legend =>
					<button
						role="button"
						tabIndex="0"
						key={legend.text}
						style={{ cursor: "pointer", outline: "none", opacity: filterLocality[legend.key] ? 1 : 0.5 }}
						onClick={() => handleFilterLocality(legend.key)}
					>
						<span className={styles.colorBox} style={{ backgroundColor: legend.color }}></span>
						{legend.text}
					</button>
				)}

			</Box>
		}



		<Grid
			container
			alignItems="center"
			columnSpacing={5}
			my={1}
			px={3}
		>
			<Grid item>
				<Typography variant="subtitle1">
					Year: {selectedYearRange.join(" - ")}
				</Typography>
			</Grid>

			<Grid item xs>
				<Slider
					min={oldestYear}
					max={currentYear}
					value={selectedYearRange}
					onChange={handleChangeYear}
					step={5}
					marks
					aria-labelledby="discrete-slider-custom"
					getAriaLabel={() => "Establishment Year Range"}
					getAriaValueText={value => value}
					valueLabelDisplay="auto"
				/>
			</Grid>
		</Grid>

		</Container>
  )
}

export default MapComponent
